import { useCallback, useMemo } from 'react';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';
import type { MutableRefObject } from 'react';
import type { Element, Page } from '@/components/canvas/document/publicTypes';
import { normalizeGridGuideSettings, resolvePageGuideSettings } from '@/components/canvas/page/guideSettings';
import { sortPagesForRender } from '@/components/canvas/workspace/model/pageArrangement';
import { PageSurface } from '@/components/canvas/page/PageSurface';
import { PageSelectionLayer } from '@/components/canvas/page/overlays/PageSelectionOverlay';
import { WorkspaceElementsLayer } from './WorkspaceElementsLayer';
import { WorkspaceElementSelectionOverlay } from '@/components/canvas/workspace-elements/WorkspaceElementSelectionOverlay';
import { PageDropTargetOverlay } from '@/components/canvas/workspace/overlays/DropTargetOverlay';
import { TextEditOverlay } from '@/components/canvas/workspace/overlays';
import { ShapeDrawPreviewLayer } from '@/components/canvas/workspace/overlays/ShapeDrawPreviewLayer';
import { TableDrawSessionLayer } from '@/components/canvas/elements/table/overlays/TableDrawSessionLayer';
import { DrawToolOverlays } from '@/components/canvas/workspace/overlays/DrawToolOverlays';
import type { TextElement as TextElementType } from '@/components/canvas/document/publicTypes';
import type { WorkspaceInteractionApi } from '@/components/canvas/workspace/controllers/useWorkspaceInteraction';

/** 落页高亮：高于工作区元素层 */
const PAGE_DROP_TARGET_OVERLAY_Z_INDEX = 1001;

export interface PagesLayerProps {
  pages: Page[];
  currentPageId: string;
  visiblePageIds: Set<string>;
  workspaceElements: Element[];
  activePageRef: MutableRefObject<HTMLDivElement | null>;
  /** Store 中的 `activePageDomRef`，指向当前激活页 `data-page` */
  activePageDomRef?: MutableRefObject<HTMLDivElement | null>;
  interaction: WorkspaceInteractionApi;
  selectedElementIds: string[];
  selectedPageIds: string[];
  zoom: number;
  hoveredElementId: string | null;
  draggingPageId: string | null;
  onPageChromeMouseDown: (pageId: string, e: React.MouseEvent) => void;
  onPageChromeClick: (pageId: string, e: React.MouseEvent) => void;
  onInactivePagePointerDown: (pageId: string, e: React.MouseEvent) => void;
}

export function PagesLayer({
  pages,
  currentPageId,
  visiblePageIds,
  workspaceElements,
  activePageRef,
  activePageDomRef,
  interaction,
  selectedElementIds,
  selectedPageIds,
  zoom,
  hoveredElementId,
  draggingPageId,
  onPageChromeMouseDown,
  onPageChromeClick,
  onInactivePagePointerDown,
}: PagesLayerProps) {
  const orderedPages = useMemo(
    () => sortPagesForRender(pages, currentPageId),
    [pages, currentPageId],
  );

  const setActivePageRef = useCallback(
    (pageId: string, el: HTMLDivElement | null) => {
      if (pageId !== currentPageId) return;
      activePageRef.current = el;
      if (activePageDomRef) activePageDomRef.current = el;
    },
    [currentPageId, activePageRef, activePageDomRef],
  );

  const mediaImageDropTargetPageId = useCanvasStore((s) => s.mediaImageDropTargetPageId);
  const dropHighlightPageId =
    interaction.ownershipDropPageId ?? mediaImageDropTargetPageId;
  const isPresentationMode = interaction.isPresentationMode;
  const isReadonlyMode = interaction.isReadonlyMode;
  const dropTargetPage = dropHighlightPageId
    ? pages.find((p) => p.id === dropHighlightPageId)
    : null;

  const editingWorkspaceTextId =
    !isReadonlyMode &&
    interaction.editingTextId &&
    workspaceElements.some(
      (e) => e.id === interaction.editingTextId && e.type === 'text',
    )
      ? interaction.editingTextId
      : null;

  const workspaceTextElements = useMemo(
    () => workspaceElements.filter((e) => e.type === 'text') as TextElementType[],
    [workspaceElements],
  );

  const pageDragHideOnSource = useMemo(() => {
    const drag = interaction.elementDrag;
    if (drag?.source !== 'page' || !drag.sourcePageId) return null;
    const crossPage =
      !!interaction.ownershipDropPageId &&
      interaction.ownershipDropPageId !== drag.sourcePageId;
    if (!interaction.pageDragEscapeToWorkspace && !crossPage) return null;
    return {
      pageId: drag.sourcePageId,
      ids: new Set(drag.startPositions.map((p) => p.id)),
    };
  }, [
    interaction.elementDrag,
    interaction.pageDragEscapeToWorkspace,
    interaction.ownershipDropPageId,
  ]);

  return (
    <div className="relative" data-workspace-world>
      {/* 层序：页板 → 工作区元素(≥1000，按元素占位) → 落页高亮(1001)；空白处不拦截，由 stage 处理工作区点击 */}
      {orderedPages.map((page) => {
        if (!visiblePageIds.has(page.id)) return null;

        const isActive = page.id === currentPageId;
        const pageIndex = pages.findIndex((p) => p.id === page.id) + 1;
        const guideSettings = resolvePageGuideSettings(page);
        const guidesVisible =
          !isReadonlyMode && guideSettings.enabled && guideSettings.system === 'grid';
        const grid = normalizeGridGuideSettings(guideSettings.grid);

        const showMarqueePageFrame =
          selectedPageIds.includes(page.id) &&
          !isActive &&
          !interaction.hideSelectionChrome;

        return (
          <div key={page.id} className="contents">
            <PageSurface
              page={page}
              pageIndex={pageIndex}
              pageCount={pages.length}
              isActive={isActive}
              isPageDragging={draggingPageId === page.id}
              grid={grid}
              guidesVisible={guidesVisible}
              interaction={isActive ? interaction : null}
              selectedElementIds={selectedElementIds}
              zoom={zoom}
              pageRef={activePageRef}
              setPageElementRef={(el) => setActivePageRef(page.id, el)}
              onDragHandleMouseDown={(e) => onPageChromeMouseDown(page.id, e)}
              onChromeClick={(e) => onPageChromeClick(page.id, e)}
              onInactivePagePointerDown={
                !isActive && !isReadonlyMode ? (e) => onInactivePagePointerDown(page.id, e) : undefined
              }
              isPresentationMode={isPresentationMode}
              isReadonlyMode={isReadonlyMode}
              workspaceDragPreviewOnPage={interaction.workspaceDragPreviewOnPage}
              hideDraggedElementIds={
                pageDragHideOnSource?.pageId === page.id
                  ? pageDragHideOnSource.ids
                  : undefined
              }
            />
            {showMarqueePageFrame && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: page.workspaceLayout.x,
                  top: page.workspaceLayout.y,
                  width: page.size.width,
                  height: page.size.height,
                  zIndex: 6,
                }}
              >
                <PageSelectionLayer
                  preview={null}
                  baseSize={page.size}
                  zoom={zoom}
                  onStartPageResize={() => {}}
                  onPageFrameDragStart={() => {}}
                />
              </div>
            )}
          </div>
        );
      })}

      <WorkspaceElementsLayer elements={workspaceElements} interaction={interaction} />

      <WorkspaceElementSelectionOverlay
        interaction={interaction}
        workspaceElements={workspaceElements}
        selectedElementIds={selectedElementIds}
        hoveredElementId={hoveredElementId}
        zoom={zoom}
        hideSelectionChrome={interaction.hideSelectionChrome}
        useWorldSelectionChrome={interaction.useWorldSelectionChrome}
        dragOffset={
          interaction.elementDrag?.source === 'workspace'
            ? interaction.dragOffset
            : { dx: 0, dy: 0 }
        }
      />

      {editingWorkspaceTextId ? (
        <TextEditOverlay
          key={editingWorkspaceTextId}
          editingTextId={editingWorkspaceTextId}
          zoom={zoom}
          allElements={workspaceTextElements}
          onCommit={(id, update) => interaction.updateElement(id, update as never)}
          onSizeChange={(id, patch) => interaction.updateElement(id, patch as never)}
          onDismiss={(id) => {
            if (interaction.editingTextIdRef.current === id) {
              interaction.setEditingTextId(null);
            }
          }}
        />
      ) : null}

      {!isReadonlyMode && !currentPageId && interaction.isTextDrawing && interaction.textDrawRect && (
        <div
          className="pointer-events-none absolute border-2 border-primary"
          style={{
            left: interaction.textDrawRect.x,
            top: interaction.textDrawRect.y,
            width: interaction.textDrawRect.width,
            height: interaction.textDrawRect.height,
            background: 'rgba(var(--accent) / 0.08)',
            zIndex: 1002,
          }}
        />
      )}

      {!isReadonlyMode && !currentPageId && interaction.isShapeDrawing && interaction.shapeDrawPreview && (
        <ShapeDrawPreviewLayer preview={interaction.shapeDrawPreview} />
      )}

      {!isReadonlyMode &&
        !currentPageId &&
        (interaction.isTableDrawing || interaction.tableDrawPending) && (
          <TableDrawSessionLayer
            zoom={zoom}
            drawRect={interaction.isTableDrawing ? interaction.tableDrawRect : null}
            pending={interaction.tableDrawPending}
            onRowsChange={interaction.setTableDrawRows}
            onColsChange={interaction.setTableDrawCols}
            onConfirm={interaction.confirmTableDraw}
            onCancel={interaction.cancelTableDraw}
          />
        )}

      {!isReadonlyMode ? (
        <DrawToolOverlays pages={pages} visiblePageIds={visiblePageIds} zoom={zoom} />
      ) : null}

      {!isReadonlyMode && dropTargetPage && (
        <div
          className="pointer-events-none absolute overflow-visible"
          style={{
            left: dropTargetPage.workspaceLayout.x,
            top: dropTargetPage.workspaceLayout.y,
            width: dropTargetPage.size.width,
            height: dropTargetPage.size.height,
            zIndex: PAGE_DROP_TARGET_OVERLAY_Z_INDEX,
          }}
        >
          <PageDropTargetOverlay
            pageWidth={dropTargetPage.size.width}
            pageHeight={dropTargetPage.size.height}
            zoom={zoom}
          />
        </div>
      )}
    </div>
  );
}
